import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Queue } from "../../hooks/useQueues";
import { getDisplayName, showsIdeaLine } from "../../lib/displayName";
import {
  OUR_READ_LABEL,
  OUR_READ_NOTE,
  OUR_READ_SECTIONS,
  filterLabel,
  groupSpec,
  specNodeMap,
  type SpecFilter,
  type SpecView,
} from "../../lib/specLines";
import { stripAnchors } from "../../lib/stripAnchors";
import { LayerGroup } from "./LayerGroup";
import { Row } from "./Row";
import { SpecDrawer } from "./SpecDrawer";

// The spec column (0051 slice 3): the idea's name, our read of it (folded;
// derived prose, not the founder's approval), the filters and one group per
// layer. It words what the server decides (lib/specLines) and decides
// nothing. The drawer opens over it and closes back to it unchanged: the
// column keeps its open layers, open areas, filter and scroll.

const FILTERS: readonly SpecFilter[] = ["all", "open", "delegated"];

const FILTER_CLASS =
  "rounded-md px-3 py-1.5 text-[13px] text-left break-words min-h-11 sm:min-h-0 transition-colors";
// Pressed is filled, the others outlined: never told by colour alone.
const FILTER_PRESSED = "border border-ink-800 bg-ink-800 text-ink-50";
const FILTER_UNPRESSED = "border border-ink-700 text-ink-300 hover:border-ink-500 hover:text-ink-100";

const IDEA_CLASS = "text-[15px] text-ink-300 leading-relaxed max-w-2xl break-words";
const IDEA_CLAMP = "max-sm:line-clamp-2";

function areaKey(ring: number, areaId: string): string {
  return `${ring}:${areaId}`;
}

// withMember is `set` with `value` in it or not, the same set when unchanged.
function withMember<T>(set: ReadonlySet<T>, value: T, member: boolean): Set<T> {
  if (set.has(value) === member) return set as Set<T>;
  const next = new Set(set);
  if (member) next.add(value);
  else next.delete(value);
  return next;
}

function nonBlank(v: unknown): v is string {
  return typeof v === "string" && v.trim() !== "";
}

export function SpecColumn({
  productId,
  spec,
  questions,
  decisions,
  inFlight,
  reveal,
}: {
  productId: string;
  spec: SpecView;
  questions?: Queue;
  decisions?: Queue;
  // A pass is in flight (the store follows a live run, or the served step
  // says working): no reflection and no settled mark (0039 N4), and the
  // drawer holds Amend.
  inFlight: boolean;
  // The bar's settled line: each new seq opens that layer and scrolls its
  // head into view.
  reveal?: { ring: number; seq: number };
}) {
  const nodeById = useMemo(() => specNodeMap(spec), [spec]);
  const currentRing = spec.layers.find((l) => l.current)?.ring;
  const defaultLayers = () => new Set<number>(currentRing === undefined ? [] : [currentRing]);

  const [filter, setFilter] = useState<SpecFilter>("all");
  const [openLayers, setOpenLayers] = useState<Set<number>>(defaultLayers);
  const [openAreas, setOpenAreas] = useState<Set<string>>(() => new Set());
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  // The row the founder opened from the column: closing the drawer returns
  // focus to its button, wherever the drawer's lines went since.
  const openedFrom = useRef<string | null>(null);
  const rowButtons = useRef(new Map<string, HTMLButtonElement>());
  const columnRef = useRef<HTMLDivElement>(null);

  const groups = useMemo(() => groupSpec(spec, filter), [spec, filter]);

  // A filter is a view act: picking "Still open" or the third opens every
  // layer and area that holds a match; "All" returns to the default view (the
  // layer in work open, every area closed). The founder's own toggles work as
  // before after either.
  const pickFilter = (f: SpecFilter) => {
    setFilter(f);
    if (f === "all") {
      setOpenLayers(defaultLayers());
      setOpenAreas(new Set());
      return;
    }
    const layers = new Set<number>();
    const areas = new Set<string>();
    for (const g of groupSpec(spec, f)) {
      if (g.layer === null) continue;
      if (g.areas.length > 0 || g.loose.length > 0) layers.add(g.layer.ring);
      for (const a of g.areas) areas.add(areaKey(g.layer.ring, a.id));
    }
    setOpenLayers(layers);
    setOpenAreas(areas);
  };

  const toggleLayer = (ring: number, open: boolean) => setOpenLayers((prev) => withMember(prev, ring, open));
  const toggleArea = (key: string, open: boolean) => setOpenAreas((prev) => withMember(prev, key, open));

  const registerRow = useCallback((id: string, el: HTMLButtonElement | null) => {
    if (el) rowButtons.current.set(id, el);
    else rowButtons.current.delete(id);
  }, []);
  const openRow = useCallback((id: string) => {
    openedFrom.current = id;
    setOpenRowId(id);
  }, []);
  const closeDrawer = useCallback(() => {
    setOpenRowId(null);
    const from = openedFrom.current;
    if (from !== null) rowButtons.current.get(from)?.focus();
  }, []);

  // The settled line: a new seq opens the layer, then, once it has rendered
  // open, scrolls its head into view.
  const revealSeq = useRef<number | undefined>(undefined);
  const [scrollRing, setScrollRing] = useState<number | null>(null);
  useEffect(() => {
    if (reveal === undefined || reveal.seq === revealSeq.current) return;
    revealSeq.current = reveal.seq;
    setOpenLayers((prev) => withMember(prev, reveal.ring, true));
    setScrollRing(reveal.ring);
  }, [reveal]);
  useEffect(() => {
    if (scrollRing === null) return;
    setScrollRing(null);
    const head = columnRef.current?.querySelector<HTMLElement>(`[data-testid="layer-head-${scrollRing}"]`);
    // jsdom has no scrollIntoView.
    if (head && typeof head.scrollIntoView === "function") {
      head.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [scrollRing]);

  // The idea line, shown by the heading's one rule (showsIdeaLine): clamped
  // to two lines on a phone, a tap shows it in full. It is a toggle only
  // where it clamps; measured only while closed, so closing it again never
  // swaps the element under the founder's focus.
  const idea = spec.meta.idea ?? "";
  const showIdea = showsIdeaLine(spec.meta);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [ideaClamped, setIdeaClamped] = useState(false);
  const ideaEl = useRef<HTMLElement | null>(null);
  const setIdeaEl = useCallback((el: HTMLElement | null) => {
    ideaEl.current = el;
  }, []);
  useLayoutEffect(() => {
    if (!showIdea || ideaOpen) return;
    const measure = () => {
      const el = ideaEl.current;
      if (el) setIdeaClamped(el.scrollHeight > el.clientHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [showIdea, ideaOpen, idea]);
  const clamp = ideaOpen ? "" : IDEA_CLAMP;

  // The drawer shows a row the read serves, none otherwise.
  const drawerRowId = openRowId !== null && spec.rows.some((r) => r.node.id === openRowId) ? openRowId : null;
  const unplaced = groups.find((g) => g.layer === null);

  // One root: the page spaces its blocks, and the drawer (fixed, over the
  // column) must take no margin from that.
  return (
    <div className="min-w-0">
      <div ref={columnRef} data-testid="spec-column" className="min-w-0 space-y-6">
        <div className="space-y-3">
          <h1
            data-testid="spec-name"
            className="text-2xl sm:text-display font-medium text-ink-50 tracking-tightest leading-tight sm:leading-[1.05] break-words"
          >
            {getDisplayName(spec.meta)}
          </h1>
          {showIdea &&
            (ideaClamped || ideaOpen ? (
              <button
                type="button"
                data-testid="spec-idea"
                aria-expanded={ideaOpen}
                onClick={() => setIdeaOpen((o) => !o)}
                className="block w-full text-left break-words min-h-11 sm:min-h-0"
              >
                <span ref={setIdeaEl} className={`block ${IDEA_CLASS} ${clamp}`}>
                  {idea}
                </span>
              </button>
            ) : (
              <p ref={setIdeaEl} data-testid="spec-idea" className={`${IDEA_CLASS} ${clamp}`}>
                {idea}
              </p>
            ))}
        </div>

        <details data-testid="our-read" className="group/read min-w-0">
          <summary className="flex min-h-11 sm:min-h-0 cursor-pointer select-none list-none items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-ink-400 hover:text-ink-200 [&::-webkit-details-marker]:hidden">
            <span
              aria-hidden="true"
              className="inline-block h-1.5 w-1.5 shrink-0 -rotate-45 border-b border-r border-ink-400 transition-transform group-open/read:rotate-45"
            />
            <span>{OUR_READ_LABEL}</span>
          </summary>
          <div className="mt-3 space-y-8">
            <p className="text-[13px] text-ink-400">{OUR_READ_NOTE}</p>
            {OUR_READ_SECTIONS.map(([key, label]) => {
              const value = spec.meta[key];
              if (!nonBlank(value)) return null;
              return (
                <section key={key}>
                  <h2 className="text-[13px] font-medium text-ink-100 mb-2 tracking-tight">{label}</h2>
                  <p className="text-[15px] text-ink-300 leading-[1.7] max-w-2xl whitespace-pre-wrap break-words">
                    {stripAnchors(value)}
                  </p>
                </section>
              );
            })}
          </div>
        </details>

        <div role="group" data-testid="spec-filters" className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => pickFilter(f)}
              className={`${FILTER_CLASS} ${filter === f ? FILTER_PRESSED : FILTER_UNPRESSED}`}
            >
              {filterLabel(f, spec.meta.product_words)}
            </button>
          ))}
        </div>

        <div className="divide-y divide-ink-800 border-y border-ink-800">
          {groups.map((g) => {
            const layer = g.layer;
            if (layer === null) return null;
            return (
              <LayerGroup
                key={layer.ring}
                layer={layer}
                group={g}
                open={openLayers.has(layer.ring)}
                onToggle={(o) => toggleLayer(layer.ring, o)}
                inFlight={inFlight}
                openAreas={openAreas}
                onToggleArea={toggleArea}
                nodeById={nodeById}
                onOpenRow={openRow}
                registerRow={registerRow}
              />
            );
          })}
        </div>

        {/* Rows whose layer the read does not serve: no head. */}
        {unplaced !== undefined && (
          <div className="space-y-0.5">
            {[...unplaced.areas.flatMap((a) => a.rows), ...unplaced.loose].map((row) => (
              <Row key={row.node.id} row={row} nodeById={nodeById} onOpen={openRow} register={registerRow} />
            ))}
          </div>
        )}
      </div>

      {drawerRowId !== null && (
        <SpecDrawer
          productId={productId}
          spec={spec}
          rowId={drawerRowId}
          questions={questions}
          decisions={decisions}
          inFlight={inFlight}
          onOpenRow={setOpenRowId}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
}
